import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Image,
  Type,
  Printer,
  Download,
  Eye,
  Upload,
  Tag,
  Percent,
  Star,
  Zap,
  Gift,
  ShoppingBag,
  Move,
  RotateCcw,
  Copy,
  Palette,
  Sparkles,
  Save,
  FolderOpen,
  ListChecks,
  Import,
  Wand2,
  Search,
  Loader2,
  Package,
  MessageSquare,
  Phone,
  Clipboard,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// WhatsApp-optimized templates (square 1:1 or portrait 4:5)
const WHATSAPP_FORMATS = [
  { id: "square", name: "Square (1:1)", width: 1080, height: 1080 },
  { id: "portrait", name: "Portrait (4:5)", width: 1080, height: 1350 },
];

// Pre-set headlines for New Arrivals
const NEW_ARRIVALS_HEADLINES = [
  "NOW IN STOCK!",
  "NEW ARRIVAL!",
  "JUST LANDED!",
  "FRESH STOCK!",
  "NEW PRODUCT!",
  "AVAILABLE NOW!",
];

const TEMPLATES = [
  { id: "a4-portrait", name: "A4 Portrait", width: 210, height: 297, scale: 3 },
  { id: "a4-landscape", name: "A4 Landscape", width: 297, height: 210, scale: 3 },
  { id: "a5-portrait", name: "A5 Flyer", width: 148, height: 210, scale: 3 },
  { id: "shelf-talker", name: "Shelf Talker", width: 100, height: 70, scale: 4 },
  { id: "poster-large", name: "Large Poster", width: 420, height: 594, scale: 2 },
];

const BADGE_ICONS = [
  { id: "tag", icon: Tag, label: "Sale Tag" },
  { id: "percent", icon: Percent, label: "Percent" },
  { id: "star", icon: Star, label: "Star" },
  { id: "zap", icon: Zap, label: "Flash Sale" },
  { id: "gift", icon: Gift, label: "Gift" },
  { id: "bag", icon: ShoppingBag, label: "Shop" },
  { id: "new", icon: Sparkles, label: "I'M NEW" },
];

const PRESET_COLORS = [
  { name: "Red Sale", primary: "#dc2626", secondary: "#fef2f2", accent: "#fbbf24" },
  { name: "Blue Special", primary: "#2563eb", secondary: "#f0f9ff", accent: "#f97316" },
  { name: "Green Deal", primary: "#16a34a", secondary: "#f0fdf4", accent: "#eab308" },
  { name: "Purple Promo", primary: "#9333ea", secondary: "#faf5ff", accent: "#f97316" },
  { name: "Orange Hot", primary: "#ea580c", secondary: "#fff7ed", accent: "#dc2626" },
  { name: "Black Friday", primary: "#171717", secondary: "#fafafa", accent: "#eab308" },
  { name: "Blue New Stock", primary: "#3b82f6", secondary: "#ffffff", accent: "#f97316" },
];

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency: 'ZAR',
    minimumFractionDigits: 2 
  }).format(value || 0);
};

export default function PromoFlyers() {
  // --- Active tab ---
  const [activeMainTab, setActiveMainTab] = useState("designer");
  
  // --- Loading states ---
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // --- Existing designer state ---
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [colorScheme, setColorScheme] = useState(PRESET_COLORS[0]);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [flyerTitle, setFlyerTitle] = useState("SPECIAL OFFER!");
  const [flyerSubtitle, setFlyerSubtitle] = useState("Limited Time Only");
  const [validDates, setValidDates] = useState("Valid while stocks last");
  const [storeName, setStoreName] = useState("BOSVELD BOU & HARDEWARE");
  const [storeContact, setStoreContact] = useState("");
  const [storeLogo, setStoreLogo] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [savedFlyers, setSavedFlyers] = useState([]);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [flyerName, setFlyerName] = useState("My Promo Flyer");
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const adCompositeRef = useRef(null);

  // --- AI Advertisement Generator state ---
  const [aiProducts, setAiProducts] = useState([]); // [{name, price, description, image, imageMode}]
  const [aiProductName, setAiProductName] = useState("");
  const [aiPrice, setAiPrice] = useState("");
  const [aiDescription, setAiDescription] = useState("");
  const [aiValidity, setAiValidity] = useState("While stocks last");
  const [aiStyle, setAiStyle] = useState("modern-clean");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratingIdx, setAiGeneratingIdx] = useState(-1);
  const [aiSavedAds, setAiSavedAds] = useState([]);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiProductSearch, setAiProductSearch] = useState("");
  const [aiProductResults, setAiProductResults] = useState([]);
  const [aiSearching, setAiSearching] = useState(false);
  const aiUploadRefs = useRef({});

  // --- New Arrivals / Product Introduction state ---
  const [naProducts, setNaProducts] = useState([]); // [{name, description, image, imageMode}]
  const [naProductName, setNaProductName] = useState("");
  const [naDescription, setNaDescription] = useState("");
  const [naHeadline, setNaHeadline] = useState("NOW IN STOCK!");
  const [naCustomHeadline, setNaCustomHeadline] = useState("");
  const [naBadgeText, setNaBadgeText] = useState("NEW IN STORE");
  const [naCtaText, setNaCtaText] = useState("Visit Us Today!");
  const [naStyle, setNaStyle] = useState("fresh-modern");
  const [naFormat, setNaFormat] = useState("square"); // square or portrait
  const [naCompanyName, setNaCompanyName] = useState("BOSVELD BOU & HARDEWARE");
  const [naContactInfo, setNaContactInfo] = useState("");
  const [naLogo, setNaLogo] = useState(null); // Company logo base64
  const [naShowProductName, setNaShowProductName] = useState(true); // Toggle to show/hide product names
  const [naGenerating, setNaGenerating] = useState(false);
  const [naGeneratingIdx, setNaGeneratingIdx] = useState(-1);
  const [naSavedFlyers, setNaSavedFlyers] = useState([]);
  const [naSaving, setNaSaving] = useState(false);
  const [naProductSearch, setNaProductSearch] = useState("");
  const [naProductResults, setNaProductResults] = useState([]);
  const [naSearching, setNaSearching] = useState(false);
  const naUploadRefs = useRef({});
  const naLogoRef = useRef(null);
  const naCompositeRef = useRef(null);

  // Load saved logo from localStorage on mount
  useEffect(() => {
    const savedLogo = localStorage.getItem("naCompanyLogo");
    if (savedLogo) {
      setNaLogo(savedLogo);
    }
  }, []);

  // Load saved flyers list on mount with timeout and error handling
  useEffect(() => {
    const loadAllData = async () => {
      setPageLoading(true);
      setLoadError(null);
      
      try {
        // Use Promise.allSettled to not fail if one endpoint fails
        const results = await Promise.allSettled([
          axios.get(`${API}/api/promo-flyers`, { timeout: 10000 }),
          axios.get(`${API}/api/ai-advertisement/saved`, { timeout: 10000 }),
          axios.get(`${API}/api/ai-new-arrivals/saved`, { timeout: 10000 }),
        ]);
        
        // Handle promo-flyers
        if (results[0].status === 'fulfilled') {
          setSavedFlyers(results[0].value.data || []);
        } else {
          console.log("Could not load saved flyers:", results[0].reason?.message);
        }
        
        // Handle AI ads
        if (results[1].status === 'fulfilled') {
          setAiSavedAds(results[1].value.data || []);
        } else {
          console.log("Could not load AI ads:", results[1].reason?.message);
        }
        
        // Handle New Arrivals
        if (results[2].status === 'fulfilled') {
          setNaSavedFlyers(results[2].value.data || []);
        } else {
          console.log("Could not load New Arrivals:", results[2].reason?.message);
        }
      } catch (error) {
        console.error("Error loading promo flyers data:", error);
        setLoadError("Some data could not be loaded. You can still create new content.");
      } finally {
        setPageLoading(false);
      }
    };
    
    loadAllData();
  }, []);

  const loadSavedFlyers = async () => {
    try {
      const res = await axios.get(`${API}/api/promo-flyers`);
      setSavedFlyers(res.data);
    } catch (error) {
      console.log("Could not load saved flyers");
    }
  };

  const loadSavedAiAds = async () => {
    try {
      const res = await axios.get(`${API}/api/ai-advertisement/saved`);
      setAiSavedAds(res.data);
    } catch (error) {
      console.log("Could not load saved AI ads");
    }
  };

  const handleAiProductSearch = async (query) => {
    setAiProductSearch(query);
    if (query.length < 2) { setAiProductResults([]); return; }
    setAiSearching(true);
    try {
      const res = await axios.get(`${API}/api/products/search?q=${encodeURIComponent(query)}&limit=8`);
      setAiProductResults(res.data || []);
    } catch { setAiProductResults([]); }
    finally { setAiSearching(false); }
  };

  const selectAiProduct = (product) => {
    setAiProductName(product.name);
    setAiPrice(`R${(product.selling_price_incl_vat || product.selling_price || 0).toFixed(2)}`);
    setAiDescription(product.description || product.name);
    setAiProductSearch("");
    setAiProductResults([]);
    toast.success(`Selected: ${product.name}`);
  };

  // Add current product to the ad list
  const handleAddProductToAd = () => {
    if (!aiProductName.trim()) { toast.error("Enter a product name"); return; }
    if (!aiPrice.trim()) { toast.error("Enter a price"); return; }
    if (aiProducts.length >= 6) { toast.error("Maximum 6 products per ad"); return; }
    setAiProducts(prev => [...prev, {
      name: aiProductName,
      price: aiPrice,
      description: aiDescription,
      image: null,
      imageMode: "ai",
    }]);
    setAiProductName("");
    setAiPrice("");
    setAiDescription("");
    setAiProductSearch("");
    toast.success("Product added to ad");
  };

  const handleRemoveProductFromAd = (index) => {
    setAiProducts(prev => prev.filter((_, i) => i !== index));
  };

  // Upload image for a specific product
  const handleProductImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAiProducts(prev => prev.map((p, i) =>
        i === index ? { ...p, image: ev.target.result, imageMode: "upload" } : p
      ));
      toast.success("Image uploaded!");
    };
    reader.readAsDataURL(file);
  };

  // Generate AI image for a specific product
  const handleGenerateProductImage = async (index) => {
    const product = aiProducts[index];
    if (!product) return;
    setAiGenerating(true);
    setAiGeneratingIdx(index);
    const toastId = toast.loading(`Generating image for ${product.name}...`);
    try {
      const res = await axios.post(`${API}/api/ai-advertisement/generate`, {
        product_name: product.name,
        price: product.price,
        description: product.description || product.name,
        validity: aiValidity,
        style: aiStyle,
      }, { timeout: 120000 });
      if (res.data?.image_base64) {
        setAiProducts(prev => prev.map((p, i) =>
          i === index ? { ...p, image: `data:image/png;base64,${res.data.image_base64}`, imageMode: "ai" } : p
        ));
        toast.dismiss(toastId);
        toast.success(`Image generated for ${product.name}!`);
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setAiGenerating(false);
      setAiGeneratingIdx(-1);
    }
  };

  // Generate AI images for ALL products without images
  const handleGenerateAllImages = async () => {
    const needImages = aiProducts.map((p, i) => ({ ...p, _idx: i })).filter(p => !p.image);
    if (needImages.length === 0) { toast.info("All products already have images"); return; }
    for (const p of needImages) {
      await handleGenerateProductImage(p._idx);
    }
  };

  // Check if ad is ready to preview
  const adHasContent = aiProducts.length > 0 && aiProducts.some(p => p.image);

  const handleDownloadAiAd = async () => {
    if (adCompositeRef.current) {
      try {
        const canvas = await html2canvas(adCompositeRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: null,
        });
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `bosveld-ad-${Date.now()}.png`;
        link.click();
        toast.success("Downloaded!");
      } catch {
        toast.error("Download failed");
      }
    }
  };

  const handleSaveAiAdComposite = async () => {
    if (!adHasContent) return;
    setAiSaving(true);
    try {
      let compositeBase64 = "";
      if (adCompositeRef.current) {
        try {
          const canvas = await html2canvas(adCompositeRef.current, {
            scale: 3, useCORS: true, backgroundColor: null,
          });
          compositeBase64 = canvas.toDataURL("image/png").split(",")[1];
        } catch { /* skip */ }
      }
      const productNames = aiProducts.map(p => p.name).join(", ");
      await axios.post(`${API}/api/ai-advertisement/save`, {
        product_name: productNames,
        price: aiProducts.map(p => p.price).join(", "),
        description: `${aiProducts.length} product(s)`,
        validity: aiValidity,
        style: aiStyle,
        image_base64: compositeBase64,
      });
      toast.success("Advertisement saved!");
      loadSavedAiAds();
    } catch {
      toast.error("Failed to save");
    } finally {
      setAiSaving(false);
    }
  };

  const handleDeleteAiAd = async (adId) => {
    try {
      await axios.delete(`${API}/api/ai-advertisement/saved/${adId}`);
      toast.success("Deleted");
      loadSavedAiAds();
    } catch { toast.error("Failed to delete"); }
  };

  const handleLoadAiAd = async (adId) => {
    try {
      const res = await axios.get(`${API}/api/ai-advertisement/saved/${adId}`);
      const ad = res.data;
      // Loaded ads are single-image composites — show as a saved preview
      if (ad.image_base64) {
        setAiProducts([{
          name: ad.product_name || "",
          price: ad.price || "",
          description: ad.description || "",
          image: `data:image/png;base64,${ad.image_base64}`,
          imageMode: "upload",
        }]);
      }
      setAiValidity(ad.validity || "");
      setAiStyle(ad.style || "modern-clean");
      toast.success("Loaded!");
    } catch { toast.error("Failed to load"); }
  };

  // =========================================================================
  // NEW ARRIVALS / PRODUCT INTRODUCTION HANDLERS
  // =========================================================================

  const loadSavedNewArrivals = async () => {
    try {
      const res = await axios.get(`${API}/api/ai-new-arrivals/saved`);
      setNaSavedFlyers(res.data);
    } catch (error) {
      console.log("Could not load saved New Arrivals flyers");
    }
  };

  const handleNaProductSearch = async (query) => {
    setNaProductSearch(query);
    if (query.length < 2) { setNaProductResults([]); return; }
    setNaSearching(true);
    try {
      const res = await axios.get(`${API}/api/products/search?q=${encodeURIComponent(query)}&limit=8`);
      setNaProductResults(res.data || []);
    } catch { setNaProductResults([]); }
    finally { setNaSearching(false); }
  };

  const selectNaProduct = (product) => {
    setNaProductName(product.name);
    setNaDescription(product.description || product.name);
    setNaProductSearch("");
    setNaProductResults([]);
    toast.success(`Selected: ${product.name}`);
  };

  const handleAddProductToNewArrivals = () => {
    if (!naProductName.trim()) { toast.error("Enter a product name"); return; }
    if (naProducts.length >= 6) { toast.error("Maximum 6 products per flyer"); return; }
    setNaProducts(prev => [...prev, {
      name: naProductName,
      description: naDescription,
      image: null,
      imageMode: "ai",
    }]);
    setNaProductName("");
    setNaDescription("");
    setNaProductSearch("");
    toast.success("Product added to flyer");
  };

  const handleRemoveProductFromNewArrivals = (index) => {
    setNaProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleNaProductImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setNaProducts(prev => prev.map((p, i) =>
        i === index ? { ...p, image: ev.target.result, imageMode: "upload" } : p
      ));
      toast.success("Image uploaded!");
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateNaProductImage = async (index) => {
    const product = naProducts[index];
    if (!product) return;
    setNaGenerating(true);
    setNaGeneratingIdx(index);
    const toastId = toast.loading(`Generating image for ${product.name}...`);
    try {
      const res = await axios.post(`${API}/api/ai-new-arrivals/generate`, {
        product_name: product.name,
        description: product.description || product.name,
        style: naStyle,
      }, { timeout: 120000 });
      if (res.data?.image_base64) {
        setNaProducts(prev => prev.map((p, i) =>
          i === index ? { ...p, image: `data:image/png;base64,${res.data.image_base64}`, imageMode: "ai" } : p
        ));
        toast.dismiss(toastId);
        toast.success(`Image generated for ${product.name}!`);
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setNaGenerating(false);
      setNaGeneratingIdx(-1);
    }
  };

  const handleGenerateAllNaImages = async () => {
    const needImages = naProducts.map((p, i) => ({ ...p, _idx: i })).filter(p => !p.image);
    if (needImages.length === 0) { toast.info("All products already have images"); return; }
    for (const p of needImages) {
      await handleGenerateNaProductImage(p._idx);
    }
  };

  // Handle clipboard paste for images (Ctrl+V or paste button)
  const handlePasteImage = async (index = null) => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(type => type.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const reader = new FileReader();
          reader.onload = (ev) => {
            const imageData = ev.target.result;
            if (index !== null && naProducts[index]) {
              // Paste to specific product
              setNaProducts(prev => prev.map((p, i) =>
                i === index ? { ...p, image: imageData, imageMode: "paste" } : p
              ));
              toast.success("Image pasted to product!");
            } else if (naProducts.length > 0) {
              // Paste to first product without image
              const emptyIdx = naProducts.findIndex(p => !p.image);
              if (emptyIdx >= 0) {
                setNaProducts(prev => prev.map((p, i) =>
                  i === emptyIdx ? { ...p, image: imageData, imageMode: "paste" } : p
                ));
                toast.success(`Image pasted!`);
              } else {
                toast.info("All products already have images. Add a new product first.");
              }
            } else {
              // No products yet - add one with the pasted image (empty name, user will edit)
              setNaProducts([{
                name: "",  // Empty name - user should enter it
                description: "",
                image: imageData,
                imageMode: "paste",
              }]);
              toast.success("Image pasted! Enter a product name below the image.");
            }
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      toast.error("No image found in clipboard. Copy an image first (screenshot or Ctrl+C on an image).");
    } catch (error) {
      if (error.name === "NotAllowedError") {
        toast.error("Clipboard access denied. Please allow clipboard permissions.");
      } else {
        toast.error("Could not read clipboard. Try using the upload button instead.");
      }
    }
  };

  // Global paste listener for the New Arrivals tab
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (activeMainTab !== "new-arrivals") return;
      
      // Check if user is typing in an input field
      const activeElement = document.activeElement;
      if (activeElement?.tagName === "INPUT" || activeElement?.tagName === "TEXTAREA") {
        return; // Don't intercept paste in input fields
      }
      
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          const reader = new FileReader();
          reader.onload = (ev) => {
            const imageData = ev.target.result;
            if (naProducts.length > 0) {
              const emptyIdx = naProducts.findIndex(p => !p.image);
              if (emptyIdx >= 0) {
                setNaProducts(prev => prev.map((p, i) =>
                  i === emptyIdx ? { ...p, image: imageData, imageMode: "paste" } : p
                ));
                toast.success(`Image pasted!`);
              } else {
                toast.info("All products have images. Add a new product first.");
              }
            } else {
              setNaProducts([{
                name: "",  // Empty name - user should enter it
                description: "",
                image: imageData,
                imageMode: "paste",
              }]);
              toast.success("Image pasted! Enter a product name.");
            }
          };
          reader.readAsDataURL(file);
          break;
        }
      }
    };
    
    document.addEventListener("paste", handleGlobalPaste);
    return () => document.removeEventListener("paste", handleGlobalPaste);
  }, [activeMainTab, naProducts]);

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const logoData = ev.target.result;
      setNaLogo(logoData);
      localStorage.setItem("naCompanyLogo", logoData);
      toast.success("Logo uploaded and saved!");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setNaLogo(null);
    localStorage.removeItem("naCompanyLogo");
    toast.success("Logo removed");
  };

  // Handle product name edit directly
  const handleProductNameEdit = (index, newName) => {
    setNaProducts(prev => prev.map((p, i) =>
      i === index ? { ...p, name: newName } : p
    ));
  };

  const naHasContent = naProducts.length > 0 && naProducts.some(p => p.image);
  const currentHeadline = naCustomHeadline.trim() || naHeadline;

  const handleDownloadNewArrivals = async () => {
    if (naCompositeRef.current) {
      try {
        const canvas = await html2canvas(naCompositeRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: null,
        });
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `new-arrivals-${Date.now()}.png`;
        link.click();
        toast.success("Downloaded!");
      } catch {
        toast.error("Download failed");
      }
    }
  };

  const handleSaveNewArrivals = async () => {
    if (!naHasContent) return;
    setNaSaving(true);
    try {
      let compositeBase64 = "";
      if (naCompositeRef.current) {
        try {
          const canvas = await html2canvas(naCompositeRef.current, {
            scale: 3, useCORS: true, backgroundColor: null,
          });
          compositeBase64 = canvas.toDataURL("image/png").split(",")[1];
        } catch { /* skip */ }
      }
      await axios.post(`${API}/api/ai-new-arrivals/save`, {
        products: naProducts.map(p => ({ name: p.name, description: p.description })),
        headline: currentHeadline,
        company_name: naCompanyName,
        contact_info: naContactInfo,
        style: naStyle,
        composite_image_base64: compositeBase64,
      });
      toast.success("Flyer saved!");
      loadSavedNewArrivals();
    } catch {
      toast.error("Failed to save");
    } finally {
      setNaSaving(false);
    }
  };

  const handleDeleteNewArrivals = async (flyerId) => {
    try {
      await axios.delete(`${API}/api/ai-new-arrivals/saved/${flyerId}`);
      toast.success("Deleted");
      loadSavedNewArrivals();
    } catch { toast.error("Failed to delete"); }
  };

  const handleLoadNewArrivals = async (flyerId) => {
    try {
      const res = await axios.get(`${API}/api/ai-new-arrivals/saved/${flyerId}`);
      const flyer = res.data;
      if (flyer.composite_image_base64) {
        // Load as single composite
        setNaProducts([{
          name: flyer.products?.map(p => p.name).join(", ") || "Loaded Flyer",
          description: "",
          image: `data:image/png;base64,${flyer.composite_image_base64}`,
          imageMode: "upload",
        }]);
      }
      setNaHeadline(flyer.headline || "NOW IN STOCK!");
      setNaCompanyName(flyer.company_name || "");
      setNaContactInfo(flyer.contact_info || "");
      setNaStyle(flyer.style || "fresh-modern");
      toast.success("Loaded!");
    } catch { toast.error("Failed to load"); }
  };

  // State for loading promotions
  const [activePromotions, setActivePromotions] = useState([]);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [loadingPromos, setLoadingPromos] = useState(false);

  const loadActivePromotions = async () => {
    setLoadingPromos(true);
    try {
      const res = await axios.get(`${API}/api/promotions?active_only=true`);
      // Filter to only show promotions with promo_products (custom price promotions)
      const promoWithProducts = res.data.filter(p => p.promo_products && p.promo_products.length > 0);
      setActivePromotions(promoWithProducts);
      setShowPromoDialog(true);
    } catch (error) {
      toast.error("Failed to load promotions");
    } finally {
      setLoadingPromos(false);
    }
  };

  const importFromPromotion = (promo) => {
    // Convert promo_products to flyer items
    const showOriginal = promo.show_original_price !== false; // default true if not set
    const newItems = promo.promo_products.map((p, index) => ({
      id: Date.now() + index,
      product_id: p.product_id || null,
      image: p.image_url || null,
      imageMode: p.image_url ? "saved" : null,  // "saved" | "ai" | "upload"
      name: p.description || p.name || "Product",
      plu_code: p.plu_code,
      originalPrice: showOriginal ? (p.original_price || 0).toFixed(2) : "",
      salePrice: (p.promo_price || 0).toFixed(2),
      description: `PLU: ${p.plu_code || 'N/A'}`,
      badge: "SALE",
      badgeIcon: "tag",
    }));

    // Add to existing items or replace
    setItems([...items, ...newItems]);
    
    // Update flyer title with promotion name
    setFlyerTitle(promo.name);
    setFlyerSubtitle(promo.description || "Special Promotional Prices");
    
    // Set valid dates from promotion
    if (promo.start_date && promo.end_date) {
      const startDate = new Date(promo.start_date).toLocaleDateString('en-ZA');
      const endDate = new Date(promo.end_date).toLocaleDateString('en-ZA');
      setValidDates(`Valid: ${startDate} - ${endDate}`);
    }

    setShowPromoDialog(false);
    toast.success(`Imported ${newItems.length} products from "${promo.name}"`);
  };

  const importAllPromotions = () => {
    let totalImported = 0;
    activePromotions.forEach(promo => {
      const showOriginal = promo.show_original_price !== false;
      const newItems = promo.promo_products.map((p, index) => ({
        id: Date.now() + totalImported + index,
        product_id: p.product_id || null,
        image: p.image_url || null,
        imageMode: p.image_url ? "saved" : null,
        name: p.description || p.name || "Product",
        plu_code: p.plu_code,
        originalPrice: showOriginal ? (p.original_price || 0).toFixed(2) : "",
        salePrice: (p.promo_price || 0).toFixed(2),
        description: `PLU: ${p.plu_code || 'N/A'}`,
        badge: "SALE",
        badgeIcon: "tag",
      }));
      totalImported += newItems.length;
      setItems(prev => [...prev, ...newItems]);
    });

    setFlyerTitle("PROMOTIONAL SPECIALS");
    setFlyerSubtitle("Multiple Promotions");
    setShowPromoDialog(false);
    toast.success(`Imported ${totalImported} products from all promotions`);
  };

  // ===== Flyer-item AI image generation =====
  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [savingAiImages, setSavingAiImages] = useState(false);

  const generateAiImageForItem = async (item) => {
    const res = await axios.post(`${API}/api/ai-advertisement/generate`, {
      product_name: item.name,
      price: item.salePrice ? parseFloat(item.salePrice) : 0,
      description: item.description || item.name,
      validity: "",
      style: "modern",
    }, { timeout: 120000 });
    if (res.data?.image_base64) {
      return `data:image/png;base64,${res.data.image_base64}`;
    }
    throw new Error("No image returned");
  };

  // Generate AI images for every flyer item that doesn't already have one.
  // The image lives ONLY in the flyer state — it is NOT saved to the product
  // until the user clicks the "Save AI images to products" confirm button.
  const handleGenerateMissingFlyerImages = async () => {
    const missing = items.filter((it) => !it.image);
    if (missing.length === 0) {
      toast.info("All flyer products already have images");
      return;
    }
    setGeneratingMissing(true);
    const toastId = toast.loading(`Generating AI images for ${missing.length} product(s)...`);
    let done = 0;
    for (const item of missing) {
      try {
        const dataUrl = await generateAiImageForItem(item);
        setItems((prev) =>
          prev.map((it) => (it.id === item.id ? { ...it, image: dataUrl, imageMode: "ai", aiUnsaved: true } : it))
        );
        done += 1;
        toast.loading(`Generated ${done} of ${missing.length}...`, { id: toastId });
      } catch (e) {
        console.error("AI image generation failed for", item.name, e);
      }
    }
    toast.dismiss(toastId);
    if (done > 0) {
      toast.success(`Generated ${done} AI image(s). Review the flyer, then click "Save AI images to products" to keep them.`);
    } else {
      toast.error("AI image generation failed for all items");
    }
    setGeneratingMissing(false);
  };

  // Permanently save freshly-generated AI images back to the source products.
  // Only acts on items flagged aiUnsaved=true with a known product_id.
  const handleSaveAiImagesToProducts = async () => {
    const toSave = items.filter((it) => it.aiUnsaved && it.image && it.product_id);
    const skipped = items.filter((it) => it.aiUnsaved && it.image && !it.product_id);
    if (toSave.length === 0) {
      if (skipped.length > 0) {
        toast.error(`No items can be saved — ${skipped.length} AI-generated item(s) are missing a product link (manually-added items can't auto-save).`);
      } else {
        toast.info("No new AI-generated images to save");
      }
      return;
    }
    if (!window.confirm(
      `Save ${toSave.length} AI-generated image(s) to their source products? This will overwrite any existing image and update the product gallery permanently.`
    )) return;

    setSavingAiImages(true);
    const toastId = toast.loading(`Saving ${toSave.length} image(s)...`);
    let saved = 0;
    let failed = 0;
    for (const item of toSave) {
      try {
        const fd = new FormData();
        fd.append("image_data", item.image);
        const res = await axios.post(`${API}/api/product-images/upload/${item.product_id}`, fd);
        if (res.data?.image_url) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? { ...it, image: res.data.image_url, imageMode: "saved", aiUnsaved: false }
                : it
            )
          );
          saved += 1;
        }
      } catch (e) {
        console.error("Save AI image failed for", item.name, e);
        failed += 1;
      }
      toast.loading(`Saved ${saved} of ${toSave.length}${failed ? ` (${failed} failed)` : ''}...`, { id: toastId });
    }
    toast.dismiss(toastId);
    if (saved > 0) {
      toast.success(`Saved ${saved} image(s) to products${skipped.length ? ` (${skipped.length} skipped — no product link)` : ''}`);
    }
    if (failed > 0) toast.error(`${failed} image(s) failed to save`);
    setSavingAiImages(false);
  };

  const saveFlyer = async () => {
    if (!flyerName.trim()) {
      toast.error("Please enter a flyer name");
      return;
    }

    setSaving(true);
    try {
      const flyerData = {
        name: flyerName,
        template: template.id,
        colorScheme: colorScheme.name,
        flyerTitle,
        flyerSubtitle,
        validDates,
        storeName,
        storeContact,
        storeLogo,
        items,
      };

      await axios.post(`${API}/api/promo-flyers`, flyerData);
      toast.success(`Flyer "${flyerName}" saved!`);
      loadSavedFlyers();
    } catch (error) {
      console.error("Error saving flyer:", error);
      toast.error("Failed to save flyer");
    } finally {
      setSaving(false);
    }
  };

  const loadFlyer = async (flyerId) => {
    try {
      const res = await axios.get(`${API}/api/promo-flyers/${flyerId}`);
      const flyer = res.data;
      
      setFlyerName(flyer.name);
      setTemplate(TEMPLATES.find(t => t.id === flyer.template) || TEMPLATES[0]);
      setColorScheme(PRESET_COLORS.find(c => c.name === flyer.colorScheme) || PRESET_COLORS[0]);
      setFlyerTitle(flyer.flyerTitle);
      setFlyerSubtitle(flyer.flyerSubtitle);
      setValidDates(flyer.validDates);
      setStoreName(flyer.storeName);
      setStoreContact(flyer.storeContact);
      setStoreLogo(flyer.storeLogo);
      setItems(flyer.items || []);
      setShowLoadDialog(false);
      toast.success(`Loaded "${flyer.name}"`);
    } catch (error) {
      toast.error("Failed to load flyer");
    }
  };

  const deleteFlyer = async (id) => {
    try {
      await axios.delete(`${API}/api/promo-flyers/${id}`);
      toast.success("Flyer deleted");
      loadSavedFlyers();
    } catch (error) {
      toast.error("Failed to delete flyer");
    }
  };

  // Note: handleLogoUpload for New Arrivals is defined earlier (line ~585)
  // This is the legacy Flyer Designer's logo handler
  const handleDesignerLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setStoreLogo(event.target.result);
        toast.success("Logo uploaded!");
      };
      reader.readAsDataURL(file);
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      image: null,
      name: "Product Name",
      originalPrice: "",
      salePrice: "",
      description: "",
      badge: "SALE",
      badgeIcon: "tag",
    };
    setItems([...items, newItem]);
    setSelectedItem(newItem.id);
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
    if (selectedItem === id) setSelectedItem(null);
  };

  const handleImageUpload = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        updateItem(id, 'image', event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const duplicateItem = (item) => {
    const newItem = {
      ...item,
      id: Date.now(),
      name: `${item.name} (Copy)`,
    };
    setItems([...items, newItem]);
    toast.success("Item duplicated");
  };

  const handlePrint = async () => {
    if (items.length === 0) {
      toast.error("No products to print. Please add products first.");
      return;
    }

    const toastId = toast.loading("Preparing print...");

    try {
      // Generate multi-page PDF and convert to print
      const pdf = await generateMultiPagePDF();
      
      toast.dismiss(toastId);

      // Convert PDF to blob and create object URL
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open print window
      const printWindow = window.open(pdfUrl, '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print");
        URL.revokeObjectURL(pdfUrl);
        return;
      }

      // Auto-trigger print when loaded
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };

      // Cleanup URL after print window closes
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000); // Clean up after 1 minute
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to prepare print: " + error.message);
      console.error("Print error:", error);
    }
  };

  // Pre-load images as data URLs for html2canvas (R2 cross-origin workaround)
  const preloadImagesForExport = async (container) => {
    if (!container) return;
    const imgs = container.querySelectorAll("img[src]");
    // Helper: wait for an <img> to finish decoding a newly-assigned src.
    // Without this, html2canvas can fire while the browser is still decoding
    // the data URL we just swapped in — the resulting canvas has empty image
    // slots even though the DOM looks correct.
    const waitForDecode = (img) =>
      new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) return resolve();
        const done = () => {
          img.removeEventListener("load", done);
          img.removeEventListener("error", done);
          resolve();
        };
        img.addEventListener("load", done);
        img.addEventListener("error", done);
        // Hard cap so a single broken URL can't hang the export.
        setTimeout(done, 4000);
      });
    const swap = async (img, dataUrl) => {
      img.src = dataUrl;
      // Prefer `decode()` when available — it resolves once the pixel buffer
      // is ready, so html2canvas will see the image.
      if (typeof img.decode === "function") {
        try {
          await img.decode();
          return;
        } catch {
          /* fall through to load-event wait */
        }
      }
      await waitForDecode(img);
    };
    const promises = Array.from(imgs).map(async (img) => {
      const src = img.src;
      if (!src || src.startsWith("data:") || src.startsWith("/")) {
        // Local / already-inlined image — just make sure it's decoded.
        await waitForDecode(img);
        return;
      }
      try {
        const resp = await fetch(src, { mode: "cors" });
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        await swap(img, dataUrl);
      } catch {
        try {
          const proxyUrl = `${API}/api/product-images/proxy?url=${encodeURIComponent(src)}`;
          const resp = await fetch(proxyUrl);
          const blob = await resp.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          await swap(img, dataUrl);
        } catch {
          // Give up on this image — leave the original src in place.
          // With `useCORS: true, allowTaint: true` html2canvas will still try
          // to render it; if the pixel data really is unreachable, the slot
          // will be blank but everything else on the flyer is preserved.
        }
      }
    });
    await Promise.all(promises);
  };

  // Smart PDF generation that fits content to A4 without cutting products
  const generateMultiPagePDF = async () => {
    const element = canvasRef.current;
    if (!element) {
      throw new Error("No flyer content found");
    }

    // Wait for images to load
    const images = element.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 5000);
        });
      })
    );

    // Convert all R2 / cross-origin images to data URLs so html2canvas can read them.
    // Without this, only locally-served images render in the PDF; CORS-tainted ones
    // come out blank (the bug where only product 1 had a picture in the export).
    await preloadImagesForExport(element);

    await new Promise(resolve => setTimeout(resolve, 300));

    // A4 dimensions in mm
    const isLandscape = template.width > template.height;
    const pageWidth = isLandscape ? 297 : 210;
    const pageHeight = isLandscape ? 210 : 297;
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);

    // Get header and product grid elements
    const headerEl = element.querySelector('.flex.items-center.justify-between');
    const productsGrid = element.querySelector('.grid');
    
    if (!productsGrid) {
      throw new Error("No products found");
    }

    // Get individual product cards
    const productCards = productsGrid.querySelectorAll('.rounded-lg, .rounded-xl, [class*="rounded"]');
    
    // Capture header once
    let headerCanvas = null;
    let headerHeightMm = 0;
    
    if (headerEl) {
      headerCanvas = await html2canvas(headerEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: colorScheme.primary,
      });
      // Calculate header height in mm based on scale
      const tempPdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      headerHeightMm = (headerCanvas.height / headerCanvas.width) * contentWidth;
    }

    // Calculate grid info
    const gridStyle = window.getComputedStyle(productsGrid);
    const gridCols = gridStyle.gridTemplateColumns.split(' ').length || template.columns || 3;

    // === LOW-MEMORY SINGLE-CAPTURE + REAL-ROW-BOUNDARY SLICING ===
    // Previous attempts:
    //  (1) Naive slice by `totalHeight/totalRows` → cut cards mid-row because
    //      CSS Grid rows aren't uniform height.
    //  (2) Render each row as its own canvas → Chrome OOM on big flyers because
    //      N high-res canvases live in memory at once.
    // Current approach: ONE html2canvas of the whole grid (memory-cheap: 1 big
    // canvas we release immediately after slicing), sliced ONLY at row-top
    // pixels measured from the DOM. Guarantees no card is split.
    // Scale kept modest (1.5) to stay well under Chrome's canvas memory cap.
    const RENDER_SCALE = 1.5;

    const gridRect0 = productsGrid.getBoundingClientRect();
    const fullGridCanvas = await html2canvas(productsGrid, {
      scale: RENDER_SCALE,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: colorScheme.secondary || '#fef2f2',
    });
    const pxToMm = contentWidth / fullGridCanvas.width;
    const fullGridHeightMm = fullGridCanvas.height * pxToMm;
    // Ratio DOM-px → canvas-px (accounts for RENDER_SCALE and any layout
    // rounding html2canvas may apply).
    const canvasScale = fullGridCanvas.width / gridRect0.width;

    // Legal cut positions in canvas pixels: 0, top-of-every-card, grid-bottom.
    // Cards in the same visual row share the same top-Y, so this set naturally
    // collapses to unique row tops.
    const cutSet = new Set();
    cutSet.add(0);
    productsGrid.querySelectorAll(':scope > *').forEach((card) => {
      const r = card.getBoundingClientRect();
      const topPx = Math.round((r.top - gridRect0.top) * canvasScale);
      if (topPx > 0) cutSet.add(topPx);
    });
    cutSet.add(fullGridCanvas.height);
    const cuts = Array.from(cutSet).sort((a, b) => a - b);

    // Available space for the products grid on each page (after the header).
    const gridSpacePerPage = contentHeight - headerHeightMm - 5;

    // Greedy pagination: pack consecutive cut ranges until adding the next
    // range would overflow the page. Overflow → close the current page at
    // the last fitting cut and start over. NEVER cut mid-row.
    const pageRanges = []; // Array<{ startPx, endPx, heightMm }>
    let startPx = 0;
    let lastGoodEndPx = null;
    for (let i = 1; i < cuts.length; i++) {
      const endPx = cuts[i];
      const heightMm = (endPx - startPx) * pxToMm;
      if (heightMm <= gridSpacePerPage) {
        lastGoodEndPx = endPx;
      } else {
        // Close the page at the previous cut; if even the very first row is
        // taller than a page, we're forced to emit it whole (row width scales
        // down when rendered anyway — the PDF just gets a slightly overflowing
        // slice; ugly but no card split).
        const closeAt = lastGoodEndPx !== null && lastGoodEndPx > startPx ? lastGoodEndPx : endPx;
        pageRanges.push({ startPx, endPx: closeAt, heightMm: (closeAt - startPx) * pxToMm });
        startPx = closeAt;
        lastGoodEndPx = null;
        if (closeAt !== endPx) i -= 1; // re-consider this cut on the new page
      }
    }
    if (startPx < fullGridCanvas.height) {
      pageRanges.push({
        startPx,
        endPx: fullGridCanvas.height,
        heightMm: (fullGridCanvas.height - startPx) * pxToMm,
      });
    }
    const totalPages = Math.max(1, pageRanges.length);

    // Create PDF
    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // If everything fits on one page
    if (totalPages <= 1) {
      let yPos = margin;

      // Add header
      if (headerCanvas) {
        const headerData = headerCanvas.toDataURL('image/jpeg', 0.9);
        pdf.addImage(headerData, 'JPEG', margin, yPos, contentWidth, headerHeightMm);
        yPos += headerHeightMm + 3;
      }

      const gridData = fullGridCanvas.toDataURL('image/jpeg', 0.9);
      pdf.addImage(gridData, 'JPEG', margin, yPos, contentWidth, fullGridHeightMm);

    } else {
      // Multi-page: slice the SINGLE full-grid canvas at DOM-measured row-top
      // pixels. Each slice canvas is created lazily and released immediately
      // after `toDataURL` so we never hold more than one slice in memory.
      for (let pageNum = 0; pageNum < totalPages; pageNum++) {
        if (pageNum > 0) pdf.addPage();
        let yPos = margin;

        // Header on every page
        if (headerCanvas) {
          const headerData = headerCanvas.toDataURL('image/jpeg', 0.9);
          pdf.addImage(headerData, 'JPEG', margin, yPos, contentWidth, headerHeightMm);
          yPos += headerHeightMm + 3;
        }

        const { startPx, endPx, heightMm: sliceHeightMm } = pageRanges[pageNum];
        const sliceHeightPx = endPx - startPx;

        // Reuse a single scratch canvas across pages — big memory win vs
        // creating a new canvas per slice.
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = fullGridCanvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        ctx.drawImage(
          fullGridCanvas,
          0, startPx,
          fullGridCanvas.width, sliceHeightPx,
          0, 0,
          fullGridCanvas.width, sliceHeightPx,
        );
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.9);
        // Release: shrink the canvas to 1px so the browser reclaims the ImageBitmap.
        sliceCanvas.width = 1;
        sliceCanvas.height = 1;

        pdf.addImage(sliceData, 'JPEG', margin, yPos, contentWidth, sliceHeightMm);

        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`Page ${pageNum + 1} of ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      // Release the full-grid canvas.
      fullGridCanvas.width = 1;
      fullGridCanvas.height = 1;
    }

    return pdf;
  };

  const handleExportPDF = async () => {
    if (items.length === 0) {
      toast.error("No products to export. Please add products first.");
      return;
    }

    const toastId = toast.loading("Generating PDF...");

    try {
      // Wait for all images to load
      const element = canvasRef.current;
      const images = element.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      // Small delay for rendering
      await new Promise(resolve => setTimeout(resolve, 300));

      const pdf = await generateMultiPagePDF();
      pdf.save(`promo-flyer-${flyerName.replace(/\s+/g, '-') || 'flyer'}-${Date.now()}.pdf`);
      
      toast.dismiss(toastId);
      toast.success("PDF exported successfully!");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to export PDF: " + error.message);
      console.error("PDF export error:", error);
    }
  };

  const resetFlyer = () => {
    if (window.confirm("Reset all flyer content?")) {
      setItems([]);
      setSelectedItem(null);
      setFlyerTitle("SPECIAL OFFER!");
      setFlyerSubtitle("Limited Time Only");
      toast.success("Flyer reset");
    }
  };

  const selectedItemData = items.find(i => i.id === selectedItem);

  // Show loading state while data is being fetched
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="promo-flyers-page">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading promotional materials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="promo-flyers-page">
      {/* Error banner if some data failed to load */}
      {loadError && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center text-sm text-yellow-800">
          {loadError}
        </div>
      )}
      
      {/* Main Header with Tab Toggle */}
      <div className="bg-white shadow-sm border-b p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Promotional Materials</h1>
              <p className="text-gray-500">Create flyers and AI-powered advertisements</p>
            </div>
          </div>
          <div className="flex gap-1 border-b -mb-4">
            <button
              onClick={() => setActiveMainTab("designer")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "designer"
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Palette className="h-4 w-4 inline mr-2" />
              Flyer Designer
            </button>
            <button
              onClick={() => setActiveMainTab("ai-ads")}
              data-testid="ai-ads-tab"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "ai-ads"
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Wand2 className="h-4 w-4 inline mr-2" />
              AI Advertisement
            </button>
            <button
              onClick={() => setActiveMainTab("new-arrivals")}
              data-testid="new-arrivals-tab"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "new-arrivals"
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Package className="h-4 w-4 inline mr-2" />
              New Arrivals
            </button>
          </div>
        </div>
      </div>

      {/* TAB: Flyer Designer */}
      {activeMainTab === "designer" && (
        <>
      {/* Designer Header Bar */}
      <div className="bg-white shadow-sm border-b p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowLoadDialog(true)}>
              <FolderOpen className="h-4 w-4 mr-2" />
              Load
            </Button>
            <Button variant="outline" onClick={saveFlyer} disabled={saving} className="bg-green-50 hover:bg-green-100 border-green-300">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" onClick={resetFlyer}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? "Edit Mode" : "Preview"}
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button onClick={handleExportPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 flex gap-4">
        {/* Left Sidebar - Controls */}
        {!showPreview && (
          <div className="w-80 space-y-4 flex-shrink-0">
            {/* Flyer Name */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Flyer Name</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={flyerName}
                  onChange={(e) => setFlyerName(e.target.value)}
                  placeholder="My Promo Flyer"
                />
              </CardContent>
            </Card>

            {/* Template Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Template Size</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={template.id} onValueChange={(id) => setTemplate(TEMPLATES.find(t => t.id === id))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...TEMPLATES].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} ({t.width}×{t.height}mm)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Color Scheme */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Color Scheme
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_COLORS.map((scheme) => (
                    <button
                      key={scheme.name}
                      onClick={() => setColorScheme(scheme)}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        colorScheme.name === scheme.name ? 'border-gray-900 scale-105' : 'border-gray-200'
                      }`}
                      title={scheme.name}
                    >
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: scheme.primary }} />
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: scheme.accent }} />
                      </div>
                      <div className="text-xs mt-1 truncate">{scheme.name}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Flyer Header */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Flyer Header</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Main Title</Label>
                  <Input
                    value={flyerTitle}
                    onChange={(e) => setFlyerTitle(e.target.value)}
                    placeholder="SPECIAL OFFER!"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subtitle</Label>
                  <Input
                    value={flyerSubtitle}
                    onChange={(e) => setFlyerSubtitle(e.target.value)}
                    placeholder="Limited Time Only"
                  />
                </div>
                <div>
                  <Label className="text-xs">Valid Dates</Label>
                  <Input
                    value={validDates}
                    onChange={(e) => setValidDates(e.target.value)}
                    placeholder="Valid 1-31 Jan 2026"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Store Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Store Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Logo Upload */}
                <div>
                  <Label className="text-xs">Store Logo</Label>
                  <div 
                    className={`mt-1 border-2 border-dashed rounded-lg p-2 text-center cursor-pointer transition-colors ${
                      storeLogo ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400'
                    }`}
                    onClick={() => logoInputRef.current?.click()}
                    onPaste={(e) => {
                      const items = e.clipboardData?.items;
                      if (items) {
                        for (let i = 0; i < items.length; i++) {
                          if (items[i].type.indexOf('image') !== -1) {
                            const blob = items[i].getAsFile();
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setStoreLogo(event.target.result);
                              toast.success("Logo pasted!");
                            };
                            reader.readAsDataURL(blob);
                            break;
                          }
                        }
                      }
                    }}
                    tabIndex={0}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleDesignerLogoUpload}
                      className="hidden"
                      ref={logoInputRef}
                    />
                    {storeLogo ? (
                      <div className="flex items-center gap-2">
                        <img src={storeLogo} alt="Logo" className="h-10 object-contain" />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setStoreLogo(null); }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        Click or Ctrl+V to add logo
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Store Name</Label>
                  <Input
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact / Address</Label>
                  <Input
                    value={storeContact}
                    onChange={(e) => setStoreContact(e.target.value)}
                    placeholder="Tel: 012 345 6789"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Products */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <CardTitle className="text-sm">Products ({items.length})</CardTitle>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadActivePromotions}
                      disabled={loadingPromos}
                      className="text-teal-600 border-teal-300 hover:bg-teal-50"
                    >
                      <ListChecks className="h-4 w-4 mr-1" />
                      Import
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateMissingFlyerImages}
                      disabled={generatingMissing || items.length === 0 || items.every((it) => it.image)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid="flyer-generate-missing-ai-btn"
                      title="Generate AI images for flyer products that don't have one. You'll preview them before they're saved."
                    >
                      {generatingMissing ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating...</>
                      ) : (
                        <>✨ AI Missing</>
                      )}
                    </Button>
                    {items.some((it) => it.aiUnsaved) && (
                      <Button
                        size="sm"
                        onClick={handleSaveAiImagesToProducts}
                        disabled={savingAiImages}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="flyer-save-ai-images-btn"
                      >
                        {savingAiImages ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <>💾 Save AI ({items.filter((it) => it.aiUnsaved).length})</>
                        )}
                      </Button>
                    )}
                    <Button size="sm" onClick={addItem}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
                {items.some((it) => it.aiUnsaved) && (
                  <p className="text-xs text-purple-700 mt-2">
                    {items.filter((it) => it.aiUnsaved).length} AI-generated image(s) are showing in preview but
                    <strong> not yet saved</strong>. Review the flyer, then click <strong>Save AI</strong> to write
                    them to the source products permanently.
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <p>Click "Add" to add products manually</p>
                    <p className="mt-1">or "Import" to pull from active promotions</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => setSelectedItem(item.id)}
                        className={`p-2 rounded-lg border cursor-pointer flex items-center gap-2 ${
                          selectedItem === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {item.image ? (
                          <img src={item.image} alt="" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                            <Image className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.name}</div>
                          <div className="text-xs text-green-600 flex items-center gap-2">
                            {item.salePrice ? formatCurrency(item.salePrice) : 'No price set'}
                            {item.aiUnsaved && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">
                                AI · not saved
                              </span>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected Item Editor */}
            {selectedItemData && (
              <Card className="border-blue-200">
                <CardHeader className="pb-3 bg-blue-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm">Edit Product</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => duplicateItem(selectedItemData)}>
                      <Copy className="h-4 w-4 mr-1" />
                      Duplicate
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-4">
                  {/* Image Upload */}
                  <div>
                    <Label className="text-xs">Product Image</Label>
                    <div 
                      className={`mt-1 border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                        selectedItemData.image ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                      onPaste={(e) => {
                        const items = e.clipboardData?.items;
                        if (items) {
                          for (let i = 0; i < items.length; i++) {
                            if (items[i].type.indexOf('image') !== -1) {
                              const blob = items[i].getAsFile();
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                updateItem(selectedItemData.id, 'image', event.target.result);
                                toast.success("Image pasted!");
                              };
                              reader.readAsDataURL(blob);
                              break;
                            }
                          }
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith('image/')) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            updateItem(selectedItemData.id, 'image', event.target.result);
                            toast.success("Image dropped!");
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => fileInputRef.current?.click()}
                      tabIndex={0}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(selectedItemData.id, e)}
                        className="hidden"
                        ref={fileInputRef}
                      />
                      
                      {selectedItemData.image ? (
                        <div className="relative">
                          <img 
                            src={selectedItemData.image} 
                            alt="Product" 
                            className="max-h-32 mx-auto rounded"
                          />
                          <Button 
                            variant="destructive" 
                            size="sm"
                            className="absolute top-0 right-0 h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateItem(selectedItemData.id, 'image', null);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-gray-500">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm font-medium">Click to upload</p>
                          <p className="text-xs text-gray-400 mt-1">
                            or <span className="text-blue-500 font-medium">Ctrl+V</span> to paste screenshot
                          </p>
                          <p className="text-xs text-gray-400">
                            or drag & drop image here
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Product Name</Label>
                    <Input
                      value={selectedItemData.name}
                      onChange={(e) => updateItem(selectedItemData.id, 'name', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Original Price (R)</Label>
                      <Input
                        type="number"
                        value={selectedItemData.originalPrice}
                        onChange={(e) => updateItem(selectedItemData.id, 'originalPrice', e.target.value)}
                        placeholder="Was"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Sale Price (R)</Label>
                      <Input
                        type="number"
                        value={selectedItemData.salePrice}
                        onChange={(e) => updateItem(selectedItemData.id, 'salePrice', e.target.value)}
                        placeholder="Now"
                        className="border-green-300 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Short Description</Label>
                    <Textarea
                      value={selectedItemData.description}
                      onChange={(e) => updateItem(selectedItemData.id, 'description', e.target.value)}
                      placeholder="e.g., 20L Premium Quality"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Badge Text</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={selectedItemData.badge}
                        onChange={(e) => updateItem(selectedItemData.id, 'badge', e.target.value)}
                        placeholder="SALE"
                        className="flex-1"
                      />
                      <Select 
                        value={selectedItemData.badgeIcon} 
                        onValueChange={(v) => updateItem(selectedItemData.id, 'badgeIcon', v)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BADGE_ICONS.map(b => {
                            const Icon = b.icon;
                            return (
                              <SelectItem key={b.id} value={b.id}>
                                <div className="flex items-center gap-1">
                                  <Icon className="h-3 w-3" />
                                  <span className="text-xs">{b.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Flyer Preview/Canvas */}
        <div className="flex-1 flex justify-center overflow-auto">
          <div 
            className="bg-white shadow-2xl"
            style={{
              width: `${template.width * template.scale}px`,
              minHeight: `${template.height * template.scale}px`,
            }}
          >
            <div 
              ref={canvasRef}
              id="flyer-print-area"
              className="w-full"
              style={{
                backgroundColor: colorScheme.secondary,
                padding: `${template.scale * 8}px`,
                minHeight: `${template.height * template.scale}px`,
              }}
            >
              {/* Flyer Header */}
              <div 
                className="flex items-center justify-between py-2 px-4 rounded-lg mb-3"
                style={{ backgroundColor: colorScheme.primary }}
              >
                {storeLogo && (
                  <img 
                    src={storeLogo} 
                    alt="Logo" 
                    style={{ height: `${template.scale * 12}px`, objectFit: 'contain' }}
                  />
                )}
                <div className={`text-center ${storeLogo ? '' : 'w-full'}`}>
                  <h1 
                    className="font-black text-white tracking-tight"
                    style={{ fontSize: `${template.scale * 10}px` }}
                  >
                    {flyerTitle}
                  </h1>
                  {flyerSubtitle && (
                    <p 
                      className="text-white/90 font-medium"
                      style={{ fontSize: `${template.scale * 4}px` }}
                    >
                      {flyerSubtitle}
                    </p>
                  )}
                </div>
                {storeLogo && <div style={{ width: `${template.scale * 12}px` }} />}
              </div>

              {/* Products Grid - 2 columns */}
              <div 
                className="grid gap-3"
                style={{
                  gridTemplateColumns: 'repeat(2, 1fr)'
                }}
              >
                {items.map((item) => {
                  const BadgeIcon = BADGE_ICONS.find(b => b.id === item.badgeIcon)?.icon || Tag;
                  const savings = item.originalPrice && item.salePrice 
                    ? ((item.originalPrice - item.salePrice) / item.originalPrice * 100).toFixed(0)
                    : null;

                  return (
                    <div 
                      key={item.id}
                      className="bg-white rounded-lg shadow-md relative"
                      onClick={() => !showPreview && setSelectedItem(item.id)}
                      style={{
                        cursor: showPreview ? 'default' : 'pointer',
                        border: selectedItem === item.id && !showPreview ? `2px solid ${colorScheme.primary}` : `1px solid #e5e7eb`
                      }}
                    >
                      {/* Badge */}
                      {item.badge && (
                        <div 
                          className="absolute top-2 left-2 px-2 py-1 rounded-full flex items-center gap-1 text-white font-bold shadow z-10"
                          style={{ 
                            backgroundColor: colorScheme.accent,
                            fontSize: `${template.scale * 3}px`
                          }}
                        >
                          <BadgeIcon style={{ width: `${template.scale * 3}px`, height: `${template.scale * 3}px` }} />
                          {item.badge}
                        </div>
                      )}

                      {/* Savings Badge — removed per user request (Feb 2026):
                          user does not want the "-X%" pill shown on flyer cards. */}

                      {/* Image Container - Fixed to prevent cropping */}
                      <div 
                        className="flex items-center justify-center"
                        style={{ 
                          height: `${template.scale * 35}px`,
                          padding: `${template.scale * 2}px`,
                          paddingTop: `${template.scale * 4}px`
                        }}
                      >
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            style={{ 
                              maxWidth: '90%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <div className="text-gray-300 text-center">
                            <Image style={{ width: `${template.scale * 10}px`, height: `${template.scale * 10}px`, margin: '0 auto' }} />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div style={{ padding: `${template.scale * 2}px` }}>
                        <h3 
                          className="font-bold text-gray-900 leading-tight"
                          style={{ fontSize: `${template.scale * 3.5}px` }}
                          title={item.name}
                        >
                          {item.name}
                        </h3>
                        
                        {item.description && (
                          <p 
                            className="text-gray-500"
                            style={{ fontSize: `${template.scale * 2.5}px` }}
                          >
                            {item.description}
                          </p>
                        )}

                        {/* Prices */}
                        <div className="flex items-baseline gap-2 mt-1">
                          {item.originalPrice && parseFloat(item.originalPrice) > 0 && (
                            <span 
                              className="text-gray-400 line-through"
                              style={{ fontSize: `${template.scale * 3}px` }}
                            >
                              R{parseFloat(item.originalPrice).toFixed(2)}
                            </span>
                          )}
                          {item.salePrice && (
                            <span 
                              className="font-black"
                              style={{ 
                                fontSize: `${template.scale * 5}px`,
                                color: colorScheme.primary
                              }}
                            >
                              R{parseFloat(item.salePrice).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {items.length === 0 && (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400"
                  style={{ 
                    minHeight: `${template.scale * 60}px`,
                    fontSize: `${template.scale * 4}px`
                  }}
                >
                  <ShoppingBag style={{ width: `${template.scale * 15}px`, height: `${template.scale * 15}px` }} />
                  <p className="mt-2">Add products to your flyer</p>
                </div>
              )}

              {/* Footer */}
              <div 
                className="mt-3 text-center py-2 rounded-lg"
                style={{ backgroundColor: colorScheme.primary }}
              >
                <p 
                  className="text-white font-bold"
                  style={{ fontSize: `${template.scale * 4}px` }}
                >
                  {storeName}
                </p>
                {storeContact && (
                  <p 
                    className="text-white/80"
                    style={{ fontSize: `${template.scale * 2.5}px` }}
                  >
                    {storeContact}
                  </p>
                )}
                {validDates && (
                  <p 
                    className="text-white/70"
                    style={{ fontSize: `${template.scale * 2}px` }}
                  >
                    {validDates}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          /* Hide everything except the flyer */
          body * {
            visibility: hidden !important;
          }
          
          /* Show the flyer */
          #flyer-print-area, #flyer-print-area * {
            visibility: visible !important;
          }
          
          /* Position and size the flyer for print */
          #flyer-print-area {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 10mm !important;
            background-color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ensure backgrounds print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Hide navigation and sidebar */
          nav, aside, header, footer, .no-print {
            display: none !important;
          }
          
          /* Remove page margins */
          @page {
            margin: 0;
            size: A4;
          }
        }
      `}</style>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Load Saved Flyer</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {savedFlyers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No saved flyers yet</p>
                <p className="text-sm">Save your first flyer to see it here</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {savedFlyers.map((flyer) => (
                  <div 
                    key={flyer.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 cursor-pointer" onClick={() => loadFlyer(flyer.id)}>
                      <div className="font-medium">{flyer.name}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(flyer.created_at).toLocaleDateString()} • {flyer.template}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => loadFlyer(flyer.id)}
                      >
                        <FolderOpen className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteFlyer(flyer.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import from Promotions Dialog */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-teal-600" />
              Import from Active Promotions
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {activePromotions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Tag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No active promotions with products</p>
                <p className="text-sm mt-1">Create a Custom Price promotion first to import products</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {activePromotions.map((promo) => (
                    <div 
                      key={promo.id}
                      className="p-3 border rounded-lg hover:bg-teal-50 hover:border-teal-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{promo.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {promo.start_date} to {promo.end_date}
                          </div>
                          <div className="text-sm text-teal-600 font-medium mt-1">
                            {promo.promo_products?.length || 0} products
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => importFromPromotion(promo)}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          Import
                        </Button>
                      </div>
                      {/* Preview of products */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {promo.promo_products?.slice(0, 5).map((p, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {p.description?.slice(0, 20) || p.plu_code}...
                          </span>
                        ))}
                        {promo.promo_products?.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{promo.promo_products.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {activePromotions.length > 1 && (
                  <div className="mt-4 pt-4 border-t">
                    <Button 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      onClick={importAllPromotions}
                    >
                      <ListChecks className="h-4 w-4 mr-2" />
                      Import All ({activePromotions.reduce((sum, p) => sum + (p.promo_products?.length || 0), 0)} products)
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* TAB: AI Advertisement Generator */}
      {activeMainTab === "ai-ads" && (
        <div className="max-w-7xl mx-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left - Controls */}
            <div className="space-y-4">
              {/* Product Entry */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Add Product
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="relative">
                    <Input
                      placeholder="Search product name or PLU..."
                      value={aiProductSearch}
                      onChange={(e) => handleAiProductSearch(e.target.value)}
                      data-testid="ai-ad-product-search"
                    />
                    {aiProductResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {aiProductResults.map((p) => (
                          <button
                            key={p.id}
                            className="w-full text-left px-3 py-2 hover:bg-purple-50 flex justify-between items-center border-b last:border-0"
                            onClick={() => selectAiProduct(p)}
                          >
                            <div>
                              <div className="font-medium text-sm">{p.name}</div>
                              <div className="text-xs text-gray-500">{p.plu_code}</div>
                            </div>
                            <div className="text-sm font-semibold text-purple-600">
                              R{(p.selling_price_incl_vat || p.selling_price || 0).toFixed(2)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {aiSearching && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg p-3 text-center text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Searching...
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Product Name *</Label>
                    <Input
                      value={aiProductName}
                      onChange={(e) => setAiProductName(e.target.value)}
                      placeholder="e.g. CPM 158 Pump"
                      data-testid="ai-ad-product-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Price *</Label>
                      <Input
                        value={aiPrice}
                        onChange={(e) => setAiPrice(e.target.value)}
                        placeholder="e.g. R2,050"
                        data-testid="ai-ad-price"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={aiDescription}
                        onChange={(e) => setAiDescription(e.target.value)}
                        placeholder="Brief specs"
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-purple-300 text-purple-600 hover:bg-purple-50"
                    onClick={handleAddProductToAd}
                    disabled={!aiProductName.trim() || !aiPrice.trim() || aiProducts.length >= 6}
                    data-testid="ai-ad-add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Advertisement ({aiProducts.length}/6)
                  </Button>
                </CardContent>
              </Card>

              {/* Products in this Ad */}
              {aiProducts.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Products ({aiProducts.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {aiProducts.map((prod, idx) => (
                      <div key={idx} className="border rounded-lg p-2 flex items-center gap-2 group">
                        {/* Thumbnail */}
                        <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {prod.image ? (
                            <img src={prod.image} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <Image className="h-5 w-5 text-gray-300" />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{prod.name}</div>
                          <div className="text-xs text-purple-600 font-bold">{prod.price}</div>
                        </div>
                        {/* Actions */}
                        <div className="flex gap-1 flex-shrink-0">
                          <input
                            ref={el => aiUploadRefs.current[idx] = el}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleProductImageUpload(idx, e)}
                          />
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            title="Upload image"
                            onClick={() => aiUploadRefs.current[idx]?.click()}
                          >
                            <Upload className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            title="Generate AI image"
                            disabled={aiGenerating}
                            onClick={() => handleGenerateProductImage(idx)}
                          >
                            {aiGenerating && aiGeneratingIdx === idx
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Wand2 className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                            onClick={() => handleRemoveProductFromAd(idx)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {/* Generate all AI images button */}
                    {aiProducts.some(p => !p.image) && (
                      <Button
                        variant="outline"
                        className="w-full mt-1"
                        onClick={handleGenerateAllImages}
                        disabled={aiGenerating}
                        data-testid="ai-ad-generate-all-btn"
                      >
                        {aiGenerating ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Wand2 className="h-4 w-4 mr-2" />Generate All AI Images</>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Ad Settings */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Ad Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Validity</Label>
                    <Input
                      value={aiValidity}
                      onChange={(e) => setAiValidity(e.target.value)}
                      placeholder="e.g. Valid till end March"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "modern-clean", name: "Modern" },
                      { id: "bold-sale", name: "Bold Sale" },
                      { id: "agricultural", name: "Agricultural" },
                      { id: "industrial", name: "Industrial" },
                    ].map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setAiStyle(s.id)}
                        className={`p-2 rounded border-2 text-xs font-medium text-center transition-all ${
                          aiStyle === s.id ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Center - Preview */}
            <div className="lg:col-span-2">
              {adHasContent ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        Advertisement Preview
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDownloadAiAd}
                          data-testid="ai-ad-download-btn"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-50 hover:bg-green-100 border-green-300"
                          onClick={handleSaveAiAdComposite}
                          disabled={aiSaving}
                          data-testid="ai-ad-save-btn"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          {aiSaving ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                      {/* Multi-product Composite Ad Template */}
                      <div
                        ref={adCompositeRef}
                        style={{
                          width: "480px",
                          minHeight: aiProducts.length === 1 ? "640px" : "auto",
                          overflow: "hidden",
                          fontFamily: "'Georgia', 'Times New Roman', serif",
                          position: "relative",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                          ...(aiStyle === "bold-sale" ? {
                            background: "linear-gradient(145deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 70%, #0f3460 100%)",
                          } : aiStyle === "agricultural" ? {
                            background: "linear-gradient(160deg, #1a1208 0%, #2d1f0e 20%, #3d2b14 50%, #2d1f0e 80%, #1a1208 100%)",
                          } : aiStyle === "industrial" ? {
                            background: "linear-gradient(160deg, #0d0d0d 0%, #1a1a1a 30%, #252525 60%, #1a1a1a 100%)",
                          } : {
                            background: "linear-gradient(160deg, #0c1a2a 0%, #152238 30%, #1b2d4a 60%, #152238 100%)",
                          }),
                        }}
                      >
                        {/* Texture overlay */}
                        <div style={{
                          position: "absolute", inset: 0, opacity: 0.08,
                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
                          pointerEvents: "none",
                        }} />
                        {/* Vignette */}
                        <div style={{
                          position: "absolute", inset: 0,
                          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)",
                          pointerEvents: "none", zIndex: 1,
                        }} />

                        <div style={{ position: "relative", zIndex: 2 }}>
                          {/* Company Header */}
                          <div style={{
                            padding: "20px 24px 14px", textAlign: "center",
                            borderBottom: aiStyle === "agricultural" ? "2px solid rgba(196,164,106,0.4)"
                              : aiStyle === "bold-sale" ? "2px solid rgba(230,57,70,0.4)"
                              : aiStyle === "industrial" ? "2px solid rgba(255,120,0,0.3)"
                              : "2px solid rgba(100,160,230,0.3)",
                          }}>
                            <div style={{
                              color: "#ffffff", fontSize: "26px", fontWeight: "800",
                              letterSpacing: "3px", textTransform: "uppercase",
                              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                            }}>BOSVELD</div>
                            <div style={{
                              color: aiStyle === "agricultural" ? "#c4a46a"
                                : aiStyle === "bold-sale" ? "#e63946"
                                : aiStyle === "industrial" ? "#ff7800"
                                : "#7ab3e0",
                              fontSize: "12px", fontWeight: "600", letterSpacing: "6px",
                              textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginTop: "2px",
                            }}>BOU & HARDWARE</div>
                          </div>

                          {/* Product Grid */}
                          {aiProducts.length === 1 ? (
                            /* Single product — full width premium layout */
                            <div>
                              <div style={{
                                padding: "20px", display: "flex", justifyContent: "center",
                              }}>
                                <div style={{
                                  background: "radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, transparent 70%)",
                                  borderRadius: "12px", padding: "16px", width: "100%",
                                  display: "flex", justifyContent: "center",
                                }}>
                                  {aiProducts[0].image && (
                                    <img src={aiProducts[0].image} alt={aiProducts[0].name}
                                      style={{
                                        maxWidth: "100%", maxHeight: "280px", objectFit: "contain",
                                        borderRadius: "8px", filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.4))",
                                      }} crossOrigin="anonymous"
                                    />
                                  )}
                                </div>
                              </div>
                              <div style={{ padding: "8px 28px 16px", textAlign: "center" }}>
                                <div style={{
                                  width: "60px", height: "2px", margin: "0 auto 14px",
                                  background: aiStyle === "agricultural" ? "#c4a46a"
                                    : aiStyle === "bold-sale" ? "#e63946"
                                    : aiStyle === "industrial" ? "#ff7800" : "#7ab3e0",
                                }} />
                                <div style={{
                                  color: "#ffffff", fontSize: "20px", fontWeight: "700",
                                  textTransform: "uppercase", letterSpacing: "1px",
                                  textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                                }}>{aiProducts[0].name}</div>
                                {aiProducts[0].description && aiProducts[0].description !== aiProducts[0].name && (
                                  <div style={{
                                    color: "rgba(255,255,255,0.65)", fontSize: "12px",
                                    marginTop: "4px", marginBottom: "12px", fontFamily: "Arial, sans-serif",
                                  }}>{aiProducts[0].description}</div>
                                )}
                                <div style={{ display: "inline-block", position: "relative", marginTop: "8px" }}>
                                  <div style={{
                                    color: "#ffffff", fontSize: "42px", fontWeight: "900",
                                    letterSpacing: "-1px", textShadow: "0 2px 12px rgba(0,0,0,0.4)",
                                  }}>{aiProducts[0].price}</div>
                                  <div style={{
                                    color: "rgba(255,255,255,0.5)", fontSize: "11px",
                                    fontFamily: "Arial, sans-serif", letterSpacing: "2px",
                                    textTransform: "uppercase", marginTop: "-2px",
                                  }}>incl. VAT</div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Multi-product grid */
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "2px",
                              padding: "12px",
                            }}>
                              {aiProducts.filter(p => p.image).map((prod, idx) => (
                                <div key={idx} style={{
                                  background: "rgba(255,255,255,0.06)",
                                  borderRadius: "8px",
                                  padding: "12px",
                                  textAlign: "center",
                                }}>
                                  <div style={{
                                    height: "120px", display: "flex",
                                    justifyContent: "center", alignItems: "center", marginBottom: "8px",
                                  }}>
                                    <img src={prod.image} alt={prod.name}
                                      style={{
                                        maxWidth: "100%", maxHeight: "120px", objectFit: "contain",
                                        borderRadius: "6px", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
                                      }} crossOrigin="anonymous"
                                    />
                                  </div>
                                  <div style={{
                                    color: "#ffffff", fontSize: "11px", fontWeight: "700",
                                    textTransform: "uppercase", lineHeight: "1.3",
                                    marginBottom: "4px",
                                  }}>{prod.name}</div>
                                  {prod.description && prod.description !== prod.name && (
                                    <div style={{
                                      color: "rgba(255,255,255,0.5)", fontSize: "9px",
                                      fontFamily: "Arial, sans-serif", marginBottom: "6px",
                                      lineHeight: "1.3",
                                    }}>{prod.description}</div>
                                  )}
                                  <div style={{
                                    color: aiStyle === "agricultural" ? "#c4a46a"
                                      : aiStyle === "bold-sale" ? "#ff6b6b"
                                      : aiStyle === "industrial" ? "#ff7800"
                                      : "#7ab3e0",
                                    fontSize: "20px", fontWeight: "900",
                                    textShadow: "0 1px 6px rgba(0,0,0,0.3)",
                                  }}>{prod.price}</div>
                                  <div style={{
                                    color: "rgba(255,255,255,0.4)", fontSize: "8px",
                                    fontFamily: "Arial, sans-serif", letterSpacing: "1px",
                                  }}>INCL. VAT</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Validity */}
                          {aiValidity && (
                            <div style={{
                              textAlign: "center", padding: "4px 20px 8px",
                              color: "rgba(255,255,255,0.45)", fontSize: "11px",
                              fontStyle: "italic", letterSpacing: "1px",
                            }}>{aiValidity}</div>
                          )}

                          {/* Footer */}
                          <div style={{
                            padding: "14px 24px", textAlign: "center",
                            borderTop: "1px solid rgba(255,255,255,0.1)",
                            background: "rgba(0,0,0,0.2)",
                          }}>
                            <div style={{
                              color: "#ffffff", fontSize: "15px", fontWeight: "700",
                              fontFamily: "Arial, sans-serif", letterSpacing: "1px",
                            }}>WhatsApp: 083 278 5937</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardContent>
                    <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                      {/* Composite Ad Template — premium design */}
                      <div
                        ref={adCompositeRef}
                        style={{
                          width: "480px",
                          minHeight: "640px",
                          borderRadius: "0px",
                          overflow: "hidden",
                          fontFamily: "'Georgia', 'Times New Roman', serif",
                          position: "relative",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                          ...(aiStyle === "bold-sale" ? {
                            background: "linear-gradient(145deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 70%, #0f3460 100%)",
                          } : aiStyle === "agricultural" ? {
                            background: "linear-gradient(160deg, #1a1208 0%, #2d1f0e 20%, #3d2b14 50%, #2d1f0e 80%, #1a1208 100%)",
                          } : aiStyle === "industrial" ? {
                            background: "linear-gradient(160deg, #0d0d0d 0%, #1a1a1a 30%, #252525 60%, #1a1a1a 100%)",
                          } : aiStyle === "minimal" ? {
                            background: "#fafafa",
                          } : {
                            background: "linear-gradient(160deg, #0c1a2a 0%, #152238 30%, #1b2d4a 60%, #152238 100%)",
                          }),
                        }}
                      >
                        {/* Texture overlay */}
                        <div style={{
                          position: "absolute",
                          inset: 0,
                          opacity: aiStyle === "minimal" ? 0.03 : 0.08,
                          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
                          pointerEvents: "none",
                        }} />

                        {/* Vignette overlay */}
                        <div style={{
                          position: "absolute",
                          inset: 0,
                          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)",
                          pointerEvents: "none",
                          zIndex: 1,
                          display: aiStyle === "minimal" ? "none" : "block",
                        }} />

                        {/* Content wrapper */}
                        <div style={{ position: "relative", zIndex: 2 }}>

                          {/* Company Header */}
                          <div style={{
                            padding: "24px 24px 16px",
                            textAlign: "center",
                            borderBottom: aiStyle === "minimal"
                              ? "2px solid #e0e0e0"
                              : aiStyle === "agricultural"
                              ? "2px solid rgba(196, 164, 106, 0.4)"
                              : aiStyle === "industrial"
                              ? "2px solid rgba(255,120,0,0.3)"
                              : aiStyle === "bold-sale"
                              ? "2px solid rgba(230,57,70,0.4)"
                              : "2px solid rgba(100,160,230,0.3)",
                          }}>
                            <div style={{
                              color: aiStyle === "minimal" ? "#1a1a1a" : "#ffffff",
                              fontSize: "28px",
                              fontWeight: "800",
                              letterSpacing: "3px",
                              textTransform: "uppercase",
                              fontFamily: "'Georgia', serif",
                              textShadow: aiStyle === "minimal" ? "none" : "0 2px 8px rgba(0,0,0,0.5)",
                            }}>
                              BOSVELD
                            </div>
                            <div style={{
                              color: aiStyle === "minimal" ? "#555"
                                : aiStyle === "agricultural" ? "#c4a46a"
                                : aiStyle === "bold-sale" ? "#e63946"
                                : aiStyle === "industrial" ? "#ff7800"
                                : "#7ab3e0",
                              fontSize: "13px",
                              fontWeight: "600",
                              letterSpacing: "6px",
                              textTransform: "uppercase",
                              fontFamily: "Arial, sans-serif",
                              marginTop: "2px",
                            }}>
                              BOU & HARDWARE
                            </div>
                          </div>

                          {/* Product Image Section */}
                          <div style={{
                            padding: "20px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}>
                            <div style={{
                              background: aiStyle === "minimal"
                                ? "#f0f0f0"
                                : "radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, transparent 70%)",
                              borderRadius: "12px",
                              padding: "16px",
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                            }}>
                              <img
                                src={aiProducts[0]?.image || ""}
                                alt={aiProductName}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "280px",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  filter: aiStyle === "minimal" ? "none" : "drop-shadow(0 8px 24px rgba(0,0,0,0.4))",
                                }}
                                crossOrigin="anonymous"
                              />
                            </div>
                          </div>

                          {/* Product Info Section */}
                          <div style={{
                            padding: "8px 28px 12px",
                            textAlign: "center",
                          }}>
                            {/* Decorative line */}
                            <div style={{
                              width: "60px",
                              height: "2px",
                              background: aiStyle === "minimal" ? "#ccc"
                                : aiStyle === "agricultural" ? "#c4a46a"
                                : aiStyle === "bold-sale" ? "#e63946"
                                : aiStyle === "industrial" ? "#ff7800"
                                : "#7ab3e0",
                              margin: "0 auto 14px",
                            }} />

                            {/* Product Name */}
                            <div style={{
                              color: aiStyle === "minimal" ? "#1a1a1a" : "#ffffff",
                              fontSize: "20px",
                              fontWeight: "700",
                              marginBottom: "6px",
                              lineHeight: "1.3",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              fontFamily: "'Georgia', serif",
                              textShadow: aiStyle === "minimal" ? "none" : "0 1px 4px rgba(0,0,0,0.3)",
                            }}>
                              {aiProductName}
                            </div>

                            {/* Description */}
                            {aiDescription && aiDescription !== aiProductName && (
                              <div style={{
                                color: aiStyle === "minimal" ? "#777" : "rgba(255,255,255,0.65)",
                                fontSize: "12px",
                                marginBottom: "16px",
                                lineHeight: "1.5",
                                fontFamily: "Arial, sans-serif",
                                letterSpacing: "0.5px",
                              }}>
                                {aiDescription}
                              </div>
                            )}

                            {/* Price */}
                            <div style={{
                              display: "inline-block",
                              position: "relative",
                              marginBottom: "10px",
                            }}>
                              {/* Price glow */}
                              <div style={{
                                position: "absolute",
                                inset: "-8px",
                                background: aiStyle === "bold-sale" ? "rgba(230,57,70,0.15)"
                                  : aiStyle === "agricultural" ? "rgba(196,164,106,0.1)"
                                  : aiStyle === "industrial" ? "rgba(255,120,0,0.1)"
                                  : "rgba(100,160,230,0.1)",
                                borderRadius: "16px",
                                filter: "blur(12px)",
                                display: aiStyle === "minimal" ? "none" : "block",
                              }} />
                              <div style={{
                                position: "relative",
                                color: aiStyle === "minimal" ? "#1a1a1a"
                                  : aiStyle === "agricultural" ? "#c4a46a"
                                  : aiStyle === "bold-sale" ? "#ffffff"
                                  : aiStyle === "industrial" ? "#ff7800"
                                  : "#ffffff",
                                fontSize: "42px",
                                fontWeight: "900",
                                fontFamily: "'Georgia', serif",
                                letterSpacing: "-1px",
                                textShadow: aiStyle === "minimal" ? "none" : "0 2px 12px rgba(0,0,0,0.4)",
                              }}>
                                {aiPrice}
                              </div>
                              <div style={{
                                color: aiStyle === "minimal" ? "#999" : "rgba(255,255,255,0.5)",
                                fontSize: "11px",
                                fontFamily: "Arial, sans-serif",
                                letterSpacing: "2px",
                                textTransform: "uppercase",
                                marginTop: "-2px",
                              }}>
                                incl. VAT
                              </div>
                            </div>

                            {/* Validity */}
                            {aiValidity && (
                              <div style={{
                                color: aiStyle === "minimal" ? "#aaa" : "rgba(255,255,255,0.45)",
                                fontSize: "11px",
                                marginTop: "8px",
                                fontStyle: "italic",
                                fontFamily: "'Georgia', serif",
                                letterSpacing: "1px",
                              }}>
                                {aiValidity}
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div style={{
                            marginTop: "12px",
                            padding: "14px 24px",
                            textAlign: "center",
                            borderTop: aiStyle === "minimal"
                              ? "2px solid #e0e0e0"
                              : aiStyle === "agricultural"
                              ? "1px solid rgba(196,164,106,0.3)"
                              : "1px solid rgba(255,255,255,0.1)",
                            background: aiStyle === "minimal" ? "#f5f5f5" : "rgba(0,0,0,0.2)",
                          }}>
                            <div style={{
                              color: aiStyle === "minimal" ? "#333" : "#ffffff",
                              fontSize: "15px",
                              fontWeight: "700",
                              fontFamily: "Arial, sans-serif",
                              letterSpacing: "1px",
                            }}>
                              WhatsApp: 083 278 5937
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full">
                  <CardContent className="flex flex-col items-center justify-center h-full min-h-[500px] text-center">
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-16 w-16 text-purple-400 animate-spin mb-4" />
                        <p className="text-lg font-medium text-gray-700">Creating product image...</p>
                        <p className="text-sm text-gray-500 mt-2">This usually takes 30-60 seconds per image</p>
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-lg font-medium text-gray-500">AI Advertisement Generator</p>
                        <p className="text-sm text-gray-400 mt-2 max-w-md">
                          Add products using the form on the left (up to 6). For each product, upload your own photo or generate an AI image. The preview will update here.
                        </p>
                        <p className="text-xs text-gray-400 mt-4">
                          Company: BOSVELD BOU & HARDWARE | WhatsApp: 083 278 5937
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Saved Ads Gallery */}
              {aiSavedAds.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Saved Advertisements ({aiSavedAds.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {aiSavedAds.map((ad) => (
                        <div
                          key={ad.id}
                          className="border rounded-lg p-2 hover:border-purple-300 hover:shadow-sm transition-all group"
                        >
                          <div
                            className="cursor-pointer"
                            onClick={() => handleLoadAiAd(ad.id)}
                          >
                            <div className="bg-gray-100 rounded h-24 flex items-center justify-center mb-2">
                              <Sparkles className="h-8 w-8 text-gray-300" />
                            </div>
                            <div className="text-xs font-medium truncate">{ad.product_name}</div>
                            <div className="text-xs text-purple-600 font-semibold">{ad.price}</div>
                            <div className="text-xs text-gray-400">{new Date(ad.created_at).toLocaleDateString()}</div>
                          </div>
                          <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleLoadAiAd(ad.id)}
                            >
                              Load
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-500"
                              onClick={() => handleDeleteAiAd(ad.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB: New Arrivals / Product Introduction (WhatsApp-optimized) */}
      {activeMainTab === "new-arrivals" && (
        <div className="max-w-7xl mx-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left - Controls */}
            <div className="space-y-4">
              {/* Product Entry */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Add Product
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="relative">
                    <Input
                      placeholder="Search product name or PLU..."
                      value={naProductSearch}
                      onChange={(e) => handleNaProductSearch(e.target.value)}
                      data-testid="na-product-search"
                    />
                    {naProductResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {naProductResults.map((p) => (
                          <button
                            key={p.id}
                            className="w-full text-left px-3 py-2 hover:bg-green-50 flex justify-between items-center border-b last:border-0"
                            onClick={() => selectNaProduct(p)}
                          >
                            <div>
                              <div className="font-medium text-sm">{p.name}</div>
                              <div className="text-xs text-gray-500">{p.plu_code}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {naSearching && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg p-3 text-center text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Searching...
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Product Name *</Label>
                    <Input
                      value={naProductName}
                      onChange={(e) => setNaProductName(e.target.value)}
                      placeholder="e.g. New Garden Tools Set"
                      data-testid="na-product-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Input
                      value={naDescription}
                      onChange={(e) => setNaDescription(e.target.value)}
                      placeholder="Brief description for AI"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-green-300 text-green-600 hover:bg-green-50"
                    onClick={handleAddProductToNewArrivals}
                    disabled={!naProductName.trim() || naProducts.length >= 6}
                    data-testid="na-add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Flyer ({naProducts.length}/6)
                  </Button>
                  
                  {/* Quick Paste Screenshot Button */}
                  <div className="pt-2 border-t mt-3">
                    <p className="text-xs text-gray-500 mb-2 text-center">Or paste a screenshot directly:</p>
                    <Button
                      variant="outline"
                      className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => handlePasteImage()}
                      data-testid="na-paste-image-btn"
                    >
                      <Clipboard className="h-4 w-4 mr-2" />
                      Paste from Clipboard (Ctrl+V)
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Products in this Flyer */}
              {naProducts.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Products ({naProducts.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {naProducts.map((prod, idx) => (
                      <div key={idx} className="border rounded-lg p-2 space-y-2 group">
                        <div className="flex items-center gap-2">
                          {/* Thumbnail */}
                          <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                            {prod.image ? (
                              <img src={prod.image} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <Image className="h-5 w-5 text-gray-300" />
                            )}
                          </div>
                          {/* Editable Name */}
                          <div className="flex-1 min-w-0">
                            <Input
                              value={prod.name}
                              onChange={(e) => handleProductNameEdit(idx, e.target.value)}
                              placeholder="Enter product name..."
                              className="h-7 text-xs"
                            />
                          </div>
                          {/* Actions */}
                          <div className="flex gap-1 flex-shrink-0">
                            <input
                              ref={el => naUploadRefs.current[idx] = el}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleNaProductImageUpload(idx, e)}
                            />
                            <Button
                              variant="ghost" size="sm" className="h-7 w-7 p-0"
                              title="Paste from clipboard"
                              onClick={() => handlePasteImage(idx)}
                            >
                              <Clipboard className="h-3.5 w-3.5 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-7 w-7 p-0"
                              title="Upload image"
                              onClick={() => naUploadRefs.current[idx]?.click()}
                            >
                              <Upload className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-7 w-7 p-0"
                              title="Generate AI image"
                              disabled={naGenerating}
                              onClick={() => handleGenerateNaProductImage(idx)}
                            >
                              {naGenerating && naGeneratingIdx === idx
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Wand2 className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                              onClick={() => handleRemoveProductFromNewArrivals(idx)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Generate all AI images button */}
                    {naProducts.some(p => !p.image) && (
                      <Button
                        variant="outline"
                        className="w-full mt-1"
                        onClick={handleGenerateAllNaImages}
                        disabled={naGenerating}
                        data-testid="na-generate-all-btn"
                      >
                        {naGenerating ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Wand2 className="h-4 w-4 mr-2" />Generate All AI Images</>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Flyer Settings */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Flyer Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Company Logo */}
                  <div>
                    <Label className="text-xs mb-2 block">Company Logo</Label>
                    <input
                      ref={naLogoRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    {naLogo ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-16 border rounded bg-white flex items-center justify-center overflow-hidden">
                          <img src={naLogo} alt="Logo" className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => naLogoRef.current?.click()}
                          >
                            Change
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500"
                            onClick={handleRemoveLogo}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        onClick={() => naLogoRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </Button>
                    )}
                  </div>

                  {/* Badge Text (editable) */}
                  <div>
                    <Label className="text-xs">Badge Text</Label>
                    <Input
                      value={naBadgeText}
                      onChange={(e) => setNaBadgeText(e.target.value)}
                      placeholder="NEW IN STORE"
                    />
                  </div>

                  {/* Headline Selection */}
                  <div>
                    <Label className="text-xs">Headline</Label>
                    <Select value={naHeadline} onValueChange={setNaHeadline}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NEW_ARRIVALS_HEADLINES.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                        <SelectItem value="custom">Custom...</SelectItem>
                      </SelectContent>
                    </Select>
                    {naHeadline === "custom" && (
                      <Input
                        className="mt-2"
                        value={naCustomHeadline}
                        onChange={(e) => setNaCustomHeadline(e.target.value)}
                        placeholder="Enter custom headline..."
                      />
                    )}
                  </div>

                  {/* Company Info */}
                  <div>
                    <Label className="text-xs">Company Name</Label>
                    <Input
                      value={naCompanyName}
                      onChange={(e) => setNaCompanyName(e.target.value)}
                      placeholder="Your store name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contact Info</Label>
                    <Input
                      value={naContactInfo}
                      onChange={(e) => setNaContactInfo(e.target.value)}
                      placeholder="e.g. 012 345 6789 | info@store.co.za"
                    />
                  </div>

                  {/* CTA Button Text (editable) */}
                  <div>
                    <Label className="text-xs">Call-to-Action Button</Label>
                    <Input
                      value={naCtaText}
                      onChange={(e) => setNaCtaText(e.target.value)}
                      placeholder="Visit Us Today!"
                    />
                  </div>

                  {/* Show Product Names Toggle */}
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Show product names on flyer</Label>
                    <button
                      onClick={() => setNaShowProductName(!naShowProductName)}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        naShowProductName ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        naShowProductName ? "translate-x-5" : "translate-x-0.5"
                      }`} />
                    </button>
                  </div>

                  {/* Format Selection */}
                  <div>
                    <Label className="text-xs mb-2 block">WhatsApp Format</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {WHATSAPP_FORMATS.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setNaFormat(f.id)}
                          className={`p-2 rounded border-2 text-xs font-medium text-center transition-all ${
                            naFormat === f.id ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {f.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Style Selection */}
                  <div>
                    <Label className="text-xs mb-2 block">Image Style</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "fresh-modern", name: "Fresh Modern" },
                        { id: "hardware-industrial", name: "Industrial" },
                        { id: "outdoor-agricultural", name: "Agricultural" },
                        { id: "warehouse-stock", name: "Warehouse" },
                      ].map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setNaStyle(s.id)}
                          className={`p-2 rounded border-2 text-xs font-medium text-center transition-all ${
                            naStyle === s.id ? "border-green-500 bg-green-50" : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Center/Right - Preview */}
            <div className="lg:col-span-2">
              {naHasContent ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4 text-green-500" />
                        New Arrivals Preview
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDownloadNewArrivals}
                          data-testid="na-download-btn"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-50 hover:bg-green-100 border-green-300"
                          onClick={handleSaveNewArrivals}
                          disabled={naSaving}
                          data-testid="na-save-btn"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          {naSaving ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Composite Flyer Preview - WhatsApp optimized */}
                    <div
                      ref={naCompositeRef}
                      className="mx-auto rounded-lg overflow-hidden shadow-lg relative"
                      style={{
                        width: naFormat === "square" ? "400px" : "400px",
                        height: naFormat === "square" ? "400px" : "500px",
                        background: "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
                      }}
                    >
                      {/* Logo in top-left corner */}
                      {naLogo && (
                        <div className="absolute top-3 left-3 z-10">
                          <div className="bg-white rounded-lg p-1 shadow-lg">
                            <img src={naLogo} alt="Logo" className="h-10 w-auto max-w-[80px] object-contain" />
                          </div>
                        </div>
                      )}

                      {/* Header with Badge and Headline */}
                      <div className="text-center py-4 px-4">
                        {naBadgeText && (
                          <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 mb-2">
                            <span className="text-white/90 text-xs font-medium tracking-wide">{naBadgeText}</span>
                          </div>
                        )}
                        <h1 className="text-white text-2xl md:text-3xl font-black tracking-tight drop-shadow-lg">
                          {currentHeadline}
                        </h1>
                      </div>

                      {/* Product Images Grid */}
                      <div className={`px-4 ${naProducts.length === 1 ? 'flex justify-center' : ''}`}>
                        {naProducts.length === 1 ? (
                          // Single product - large centered
                          <div className="bg-white rounded-xl p-3 shadow-xl max-w-[280px]">
                            {naProducts[0].image && (
                              <img
                                src={naProducts[0].image}
                                alt={naProducts[0].name || "Product"}
                                className="w-full h-48 object-contain rounded-lg"
                              />
                            )}
                            {naShowProductName && naProducts[0].name && (
                              <p className="text-center text-gray-800 font-bold text-sm mt-2 truncate">{naProducts[0].name}</p>
                            )}
                          </div>
                        ) : naProducts.length <= 2 ? (
                          // 2 products - side by side
                          <div className="grid grid-cols-2 gap-3">
                            {naProducts.filter(p => p.image).map((prod, idx) => (
                              <div key={idx} className="bg-white rounded-xl p-2 shadow-xl">
                                <img src={prod.image} alt={prod.name || "Product"} className="w-full h-28 object-contain rounded-lg" />
                                {naShowProductName && prod.name && (
                                  <p className="text-center text-gray-800 font-bold text-xs mt-1 truncate">{prod.name}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : naProducts.length <= 4 ? (
                          // 3-4 products - 2x2 grid
                          <div className="grid grid-cols-2 gap-2">
                            {naProducts.filter(p => p.image).map((prod, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-2 shadow-lg">
                                <img src={prod.image} alt={prod.name || "Product"} className="w-full h-20 object-contain rounded" />
                                {naShowProductName && prod.name && (
                                  <p className="text-center text-gray-800 font-semibold text-xs mt-1 truncate">{prod.name}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          // 5-6 products - 3x2 grid
                          <div className="grid grid-cols-3 gap-2">
                            {naProducts.filter(p => p.image).map((prod, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-1.5 shadow-lg">
                                <img src={prod.image} alt={prod.name || "Product"} className="w-full h-16 object-contain rounded" />
                                {naShowProductName && prod.name && (
                                  <p className="text-center text-gray-800 font-semibold text-[10px] mt-1 truncate">{prod.name}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer with Company Info */}
                      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm py-3 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {/* Logo in footer too (smaller) */}
                            {naLogo && (
                              <img src={naLogo} alt="Logo" className="h-8 w-auto max-w-[50px] object-contain" />
                            )}
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{naCompanyName}</p>
                              {naContactInfo && (
                                <p className="text-gray-600 text-xs flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {naContactInfo}
                                </p>
                              )}
                            </div>
                          </div>
                          {naCtaText && (
                            <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                              {naCtaText}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-96 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Package className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">Add products and generate images</p>
                    <p className="text-sm">to preview your New Arrivals flyer</p>
                  </div>
                </Card>
              )}

              {/* Saved Flyers */}
              {naSavedFlyers.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Saved New Arrivals Flyers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {naSavedFlyers.map((flyer) => (
                        <div
                          key={flyer.id}
                          className="group relative border rounded-lg p-2 cursor-pointer hover:border-green-300 transition-colors"
                        >
                          <div
                            className="text-center"
                            onClick={() => handleLoadNewArrivals(flyer.id)}
                          >
                            <div className="bg-green-100 rounded h-24 flex items-center justify-center mb-2">
                              <Package className="h-8 w-8 text-green-300" />
                            </div>
                            <div className="text-xs font-medium truncate">{flyer.headline}</div>
                            <div className="text-xs text-gray-500">{flyer.products?.length || 0} product(s)</div>
                            <div className="text-xs text-gray-400">{new Date(flyer.created_at).toLocaleDateString()}</div>
                          </div>
                          <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => handleLoadNewArrivals(flyer.id)}
                            >
                              Load
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-500"
                              onClick={() => handleDeleteNewArrivals(flyer.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
